/**
 * One table for metric rows, shared by the owner dashboard and the
 * public share page — the two copies had already drifted (the shared one
 * lost percent formatting), so any future fix lands once (PR #11 review).
 */

import { Text } from '@shopify/polaris';

import type { MetricRow } from '../../types/marketingSuite';
import { formatMetricValue } from './MiniChart';

const RESERVED = new Set(['period_end', 'value', 'prior_value']);

export function MetricTable({
  rows,
  unit,
}: {
  rows: MetricRow[];
  unit: string;
}) {
  if (!rows.length) {
    return (
      <Text as="p" tone="subdued">
        No data yet.
      </Text>
    );
  }
  // Dimension columns come from the rows themselves — a fixed
  // platform/currency list rendered indistinguishable duplicate rows for
  // any other grouping (PR #11 review).
  const dims = Array.from(
    new Set(rows.flatMap((r) => Object.keys(r)))
  ).filter((d) => !RESERVED.has(d) && rows.some((r) => r[d] != null));
  // Window by PERIODS, not rows: a raw row slice on grouped results
  // truncated history (12 periods x 2 currencies showed 6 periods) and
  // could drop groups from a period (PR #11 review).
  const periods: (string | undefined)[] = [];
  for (const r of rows) {
    if (!periods.includes(r.period_end)) periods.push(r.period_end);
  }
  const keep = new Set(periods.slice(-12));
  const visible = rows.filter((r) => keep.has(r.period_end));
  const cell = (r: MetricRow, v: number | undefined | null) =>
    v != null ? formatMetricValue(v, unit, r.currency) : '—';
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            {['period', ...dims, 'value', 'prior'].map((h) => (
              <th
                key={h}
                style={{
                  textAlign: 'left',
                  padding: '4px 8px',
                  borderBottom: '1px solid var(--p-color-border)',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((r, i) => (
            <tr key={i}>
              <td style={{ padding: '4px 8px' }}>{r.period_end}</td>
              {dims.map((d) => (
                <td key={d} style={{ padding: '4px 8px' }}>
                  {String(r[d] ?? '')}
                </td>
              ))}
              <td style={{ padding: '4px 8px' }}>{cell(r, r.value)}</td>
              <td style={{ padding: '4px 8px' }}>{cell(r, r.prior_value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
